import React, { useRef, useState } from 'react';
import { CircleNotch, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { captureVisibleW37 } from './visibleExportSnapshot';
import { buildVisibleWorkbook, visibleExportFilename } from './visibleExportWorkbook';

export const W37VisibleExcelButton = ({ rootRef, view, data, stale }) => {
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  if (!['summary', 'detail'].includes(view)) return null;
  const notify = (kind, message) => toast[kind](
    <span data-testid={`w37-excel-${kind}`}>{message}</span>, { id: 'w37-excel-feedback' },
  );
  const download = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      // Capture synchronously BEFORE loading Excel or changing the UI. Scrolling,
      // switching tabs or the existing refresh cannot change this export's rows.
      const snapshot = captureVisibleW37({
        root: rootRef.current, view, stale,
        sourceDraw: data?.recent_draw?.draw_number,
        sourceDate: data?.recent_draw?.draw_date,
      });
      if (!snapshot.sections.length) {
        notify('info', 'No visible rows to export.');
        return;
      }
      setBusy(true);
      const XLSX = await import('xlsx');
      XLSX.writeFile(buildVisibleWorkbook(XLSX, snapshot), visibleExportFilename(snapshot), { compression: true });
      const count = snapshot.sections.reduce((sum, section) => sum + section.rows.length, 0);
      notify('success', `Excel downloaded · ${count} visible rows`);
    } catch (error) {
      notify('error', 'Excel download failed. Please try again.');
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <Button onClick={download} disabled={busy || !data} variant="outline"
      className="shrink-0 h-9 w-9 sm:w-auto sm:min-w-[172px] px-2 sm:px-3 gap-2 border-amber-300 text-amber-900 bg-white hover:bg-amber-50"
      aria-label="Download Excel — visible rows only" title="Download Excel — visible rows only"
      aria-busy={busy} data-testid="w37-export-visible-excel">
      {busy ? <CircleNotch size={16} className="animate-spin shrink-0" /> : <DownloadSimple size={16} className="shrink-0" />}
      <span className="hidden sm:inline" data-testid="w37-export-visible-excel-label">{busy ? 'Preparing Excel…' : 'Excel · visible rows'}</span>
    </Button>
  );
};
